import { useState } from "react";
import LoginScreen from "./components/LoginScreen";
import ElicitationChat from "./components/ElicitationChat";
import PerformanceDashboard from "./components/PerformanceDashboard";
import BayesianPipeline from "./components/BayesianPipeline";
import ExpertInterview, { ExtractedInsight } from "./components/ExpertInterview";
import InterviewResults from "./components/InterviewResults";
import ElicitationProtocol from "./components/ElicitationProtocol";
import VariablesTab from "./components/VariablesTab";
import QualitativeTab from "./components/QualitativeTab";
import { FeatureDefinition, QualitativeResult } from "./api";
import WorkflowTracker from "./components/WorkflowTracker";
import VariableResolutionTab from "./components/VariableResolution";
import VariableRegistry from "./components/VariableRegistry";
import KnowledgeBase from "./components/KnowledgeBase";
import VariableReview from "./components/VariableReview";
import DataAudit from "./components/DataAudit";
import VariableStaging from "./components/VariableStaging";
import AuditTrailPanel from "./components/AuditTrailPanel";
import WhatIfPanel from "./components/WhatIfPanel";
import DomainTemplates from "./components/DomainTemplates";
import SemanticSearch from "./components/SemanticSearch";
import DemoFlow from "./components/DemoFlow";
import DatasetMatcher from "./components/DatasetMatcher";
import ExpertFinder from "./components/ExpertFinder";

/* ── Nav Dropdown ── */
function NavDropdown({ label, activeTab, tabs, onSelect }: {
  label: string;
  activeTab: string;
  tabs: { key: string; label: string; desc: string }[];
  onSelect: (tab: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const isActive = tabs.some(t => t.key === activeTab);
  const activeSub = tabs.find(t => t.key === activeTab);

  return (
    <div
      style={{ position: "relative" }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        className={`nav-pill ${isActive ? "active" : ""}`}
        style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
        onClick={() => { if (tabs.length > 0) onSelect(tabs[0].key); }}
      >
        {activeSub ? activeSub.label : label}
        <svg width="8" height="8" viewBox="0 0 10 10" style={{ opacity: 0.4 }}>
          <polyline points="2,3 5,7 8,3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      {open && tabs.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, marginTop: 4,
          background: "#fff", borderRadius: 10, padding: "6px 0",
          boxShadow: "0 4px 20px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)",
          minWidth: 220, zIndex: 100,
        }}>
          {tabs.map(tab => (
            <div
              key={tab.key}
              onClick={() => { onSelect(tab.key); setOpen(false); }}
              style={{
                padding: "8px 16px", cursor: "pointer",
                background: activeTab === tab.key ? "rgba(0,0,0,0.04)" : "transparent",
                transition: "background 0.1s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.04)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = activeTab === tab.key ? "rgba(0,0,0,0.04)" : "transparent"; }}
            >
              <div style={{ fontSize: 12, fontWeight: activeTab === tab.key ? 700 : 500, display: "flex", alignItems: "center", gap: 6 }}>
                {tab.label}
                {activeTab === tab.key && (
                  <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#2d6bc4" }} />
                )}
              </div>
              <div style={{ fontSize: 10, color: "rgba(0,0,0,0.4)", marginTop: 1 }}>{tab.desc}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface InterviewData {
  insights: ExtractedInsight[];
  expertName: string;
  duration: number;
}

type Tab = "home" | "interview" | "results" | "elicitation" | "variables" | "qualitative" | "benchmark" | "resolution" | "performance" | "pipeline" | "registry" | "knowledge" | "review" | "audit" | "staging" | "audit-trail" | "whatif" | "templates" | "search" | "demo" | "matcher" | "find-experts";

function App() {
  const [authed, setAuthed] = useState(false);
  const [activeFeature, setActiveFeature] = useState<FeatureDefinition | null>(null);
  const [collectedFeatures, setCollectedFeatures] = useState<FeatureDefinition[]>([]);
  const [activeTab, setActiveTab] = useState<Tab>("home");
  const [interviewData, setInterviewData] = useState<InterviewData | null>(null);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [pipelineStep, setPipelineStep] = useState(0);
  const [interviewTranscript, setInterviewTranscript] = useState<string>("");
  const [qualResult, setQualResult] = useState<QualitativeResult | null>(null);

  const handleInterviewFinish = (insights: ExtractedInsight[], expert: string, duration: number, transcript: string) => {
    setInterviewData({ insights, expertName: expert, duration });
    setInterviewTranscript(transcript);
    setActiveTab("results");
  };

  const handleElicitationComplete = (prior: { mean: number; std: number; certainty: number }) => {
    console.log("Aggregated prior:", prior);
    setActiveTab("home");
  };

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: "transparent" }}>
      {/* Top nav — Glassmorphic */}
      <header className="glass-strong" style={{
        padding: "14px 32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexShrink: 0,
        borderBottom: "1px solid rgba(255,255,255,0.3)",
        zIndex: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <img
            src="/tacit-logo.png"
            alt="Tacit"
            style={{ height: 28, width: "auto", marginRight: 14 }}
          />

          <button
            onClick={() => setActiveTab("home")}
            className={`nav-pill ${activeTab === "home" ? "active" : ""}`}
            style={{ fontFamily: "'Merriweather', serif", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 7 }}
          >
            Home
          </button>

          <button
            onClick={() => setActiveTab("demo")}
            className={`nav-pill ${activeTab === "demo" ? "active" : ""}`}
            style={{
              background: activeTab === "demo" ? "linear-gradient(135deg, #1e3a6e, #3b7dd8)" : "rgba(30,58,110,0.06)",
              color: activeTab === "demo" ? "#fff" : "#1e3a6e",
              fontWeight: 700, fontSize: 11,
              border: activeTab === "demo" ? "none" : "1px solid rgba(30,58,110,0.15)",
            }}
          >
            ▶ Demo
          </button>

          <NavDropdown
            label="Experts"
            activeTab={activeTab}
            tabs={[
              { key: "find-experts", label: "Find Experts", desc: "Search our network by model type and schema" },
              { key: "interview", label: "Interview an Expert", desc: "Live conversation with real-time extraction" },
              ...(interviewData ? [
                { key: "results", label: "Extracted Signals", desc: `${interviewData.insights.length} variables from last interview` },
                { key: "elicitation", label: "SHELF Calibration", desc: "Multi-expert consensus on key variables" },
              ] : []),
            ]}
            onSelect={(tab) => setActiveTab(tab as Tab)}
          />

          <NavDropdown
            label="Data"
            activeTab={activeTab}
            tabs={[
              { key: "matcher", label: "Dataset Scanner", desc: "Upload Excel/CSV, match expert variables to columns" },
              { key: "audit", label: "Domain Audit", desc: "Ontology vs. dataset gap analysis" },
              { key: "resolution", label: "Resolution", desc: "Map each expert variable to a data column" },
              { key: "staging", label: "Feature Engineering", desc: "Engineer new features from expert thresholds" },
              { key: "variables", label: "All Variables", desc: "View and manage your variable catalog" },
            ]}
            onSelect={(tab) => setActiveTab(tab as Tab)}
          />

          <NavDropdown
            label="Model"
            activeTab={activeTab}
            tabs={[
              { key: "performance", label: "Model Impact", desc: "Before/after comparison with expert features" },
              { key: "pipeline", label: "Bayesian Pipeline", desc: "Full prior → posterior → shadow model" },
              { key: "whatif", label: "What-If Analysis", desc: "Stress test variables with perturbations" },
            ]}
            onSelect={(tab) => setActiveTab(tab as Tab)}
          />

          <NavDropdown
            label="Library"
            activeTab={activeTab}
            tabs={[
              { key: "registry", label: "Variable Registry", desc: "All variables across projects and experts" },
              { key: "knowledge", label: "Knowledge Base", desc: "Searchable expert insights and transcript chunks" },
              { key: "audit-trail", label: "Audit Trail", desc: "Full event log with source citations" },
            ]}
            onSelect={(tab) => setActiveTab(tab as Tab)}
          />

        </div>

      </header>

      {/* Main content + workflow sidebar */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Page content */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden", minWidth: 0 }}>
          {activeTab === "demo" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <DemoFlow onExit={() => setActiveTab("home")} />
            </div>
          ) : activeTab === "interview" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <ExpertInterview
                onFinish={handleInterviewFinish}
                onBack={() => setActiveTab("home")}
                onFeatureCreated={(f) => { setActiveFeature(f); setCollectedFeatures(prev => prev.some(p => p.feature_id === f.feature_id) ? prev : [...prev, f]); }}
                onQualitativeReady={(r) => setQualResult(r)}
              />
            </div>
          ) : activeTab === "results" && interviewData ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <InterviewResults
                insights={interviewData.insights}
                expertName={interviewData.expertName}
                duration={interviewData.duration}
                onBack={() => setActiveTab("interview")}
              />
            </div>
          ) : activeTab === "variables" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <VariablesTab liveFeatures={collectedFeatures} />
            </div>
          ) : activeTab === "qualitative" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <QualitativeTab
                transcript={interviewTranscript}
                qualResult={qualResult}
              />
            </div>
          ) : activeTab === "resolution" && interviewData ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <VariableResolutionTab
                insights={interviewData.insights}
                expertName={interviewData.expertName}
              />
            </div>
          ) : activeTab === "registry" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <VariableRegistry />
            </div>
          ) : activeTab === "find-experts" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <ExpertFinder />
            </div>
          ) : activeTab === "matcher" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <DatasetMatcher />
            </div>
          ) : activeTab === "audit" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <DataAudit onStartInterview={() => { setActiveTab("interview"); }} />
            </div>
          ) : activeTab === "review" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <VariableReview />
            </div>
          ) : activeTab === "staging" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <VariableStaging />
            </div>
          ) : activeTab === "audit-trail" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <AuditTrailPanel />
            </div>
          ) : activeTab === "whatif" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <WhatIfPanel feature={activeFeature} collectedFeatures={collectedFeatures} />
            </div>
          ) : activeTab === "templates" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <DomainTemplates onFeaturesCreated={(features) => {
                setCollectedFeatures(prev => [...prev, ...features]);
                if (features.length > 0) setActiveFeature(features[0]);
              }} />
            </div>
          ) : activeTab === "search" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <SemanticSearch />
            </div>
          ) : activeTab === "knowledge" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <KnowledgeBase />
            </div>
          ) : activeTab === "performance" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <PerformanceDashboard feature={activeFeature} />
            </div>
          ) : activeTab === "pipeline" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <BayesianPipeline
                featureName={activeFeature?.name ?? "Expert_Variable"}
                featureId={activeFeature?.feature_id}
                priorMean={activeFeature?.bayesian_prior?.mean}
                priorStd={activeFeature?.bayesian_prior?.std_dev}
                expertCertainty={activeFeature?.bayesian_prior?.expert_certainty}
                onStepChange={(step) => setPipelineStep(step)}
              />
            </div>
          ) : activeTab === "elicitation" ? (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <ElicitationProtocol
                variable={{ name: activeFeature?.name ?? "Expert_Variable", type: activeFeature?.encoding ?? "TBD", levels: "-" }}
                onComplete={handleElicitationComplete}
                onBack={() => setActiveTab("results")}
              />
            </div>
          ) : (
            /* ── Home — Dashboard ── */
            <div style={{ flex: 1, overflow: "auto", background: "#fff" }}>
              <div style={{ maxWidth: 800, margin: "0 auto", padding: "48px 32px" }}>
                <h1 style={{ fontSize: 24, fontWeight: 300, fontFamily: "'Merriweather', serif", margin: "0 0 6px" }}>
                  Welcome to Tacit
                </h1>
                <p style={{ fontSize: 13, color: "rgba(0,0,0,0.5)", margin: "0 0 36px" }}>
                  Interview domain experts. Extract structured features. Prove they improve your model.
                </p>

                {/* Two action cards */}
                <div style={{ display: "flex", gap: 16, marginBottom: 36 }}>
                  <div
                    onClick={() => setActiveTab("interview")}
                    style={{
                      flex: 1, padding: "28px 24px", borderRadius: 16, cursor: "pointer",
                      border: "1.5px solid rgba(30,58,110,0.12)", background: "rgba(30,58,110,0.02)",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "#1e3a6e"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(30,58,110,0.1)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(30,58,110,0.12)"; e.currentTarget.style.boxShadow = "none"; }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1e3a6e" strokeWidth="1.5" strokeLinecap="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></svg>
                      <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: "rgba(30,58,110,0.1)", color: "#1e3a6e", textTransform: "uppercase", letterSpacing: "0.06em" }}>Network</span>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Find an expert</div>
                    <div style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", lineHeight: 1.6 }}>
                      Tell us what you're modeling. We match you with a data scientist or domain specialist from our network.
                    </div>
                  </div>
                  <div
                    onClick={() => setActiveTab("interview")}
                    style={{
                      flex: 1, padding: "28px 24px", borderRadius: 16, cursor: "pointer",
                      border: "1px solid rgba(0,0,0,0.08)", background: "#fff",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(0,0,0,0.2)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.06)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(0,0,0,0.08)"; e.currentTarget.style.boxShadow = "none"; }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.5" strokeLinecap="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                      <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: "rgba(0,0,0,0.05)", color: "rgba(0,0,0,0.5)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Internal</span>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Bring your own expert</div>
                    <div style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", lineHeight: 1.6 }}>
                      Interview someone from your organization — a domain specialist, team lead, or subject matter expert.
                    </div>
                  </div>
                </div>

                {/* Recent activity / quick links */}
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(0,0,0,0.3)", marginBottom: 12 }}>Quick actions</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 36 }}>
                  {[
                    { label: "Upload dataset", tab: "audit" as Tab, icon: "↑" },
                    { label: "Browse experts", tab: "interview" as Tab, icon: "→" },
                    { label: "View variables", tab: "variables" as Tab, icon: "◇" },
                    { label: "Run demo", tab: "demo" as Tab, icon: "▶" },
                  ].map(a => (
                    <button key={a.label} onClick={() => setActiveTab(a.tab)} style={{
                      padding: "10px 18px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.08)",
                      background: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 500,
                      display: "flex", alignItems: "center", gap: 6, color: "rgba(0,0,0,0.6)",
                      transition: "all 0.15s ease",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(0,0,0,0.2)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(0,0,0,0.08)"; }}
                    >
                      <span style={{ fontSize: 14 }}>{a.icon}</span>
                      {a.label}
                    </button>
                  ))}
                </div>

                {/* Stats summary */}
                <div style={{ display: "flex", gap: 16 }}>
                  {[
                    { num: collectedFeatures.length, label: "Variables collected" },
                    { num: interviewData ? interviewData.insights.length : 0, label: "From last interview" },
                    { num: "—", label: "Model impact" },
                  ].map((s, i) => (
                    <div key={i} style={{ flex: 1, padding: "18px 20px", borderRadius: 12, background: "rgba(0,0,0,0.02)" }}>
                      <div style={{ fontSize: 28, fontWeight: 300, fontFamily: "'IBM Plex Mono', monospace", color: "#1e3a6e" }}>{s.num}</div>
                      <div style={{ fontSize: 11, color: "rgba(0,0,0,0.4)", marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Workflow tracker — only show on non-demo, non-home tabs */}
        {activeTab !== "demo" && activeTab !== "home" && (
          <WorkflowTracker
            feature={activeFeature}
            activeTab={activeTab}
            pipelineStep={pipelineStep}
            onNavigate={(tab, panel) => {
              setActiveTab(tab as Tab);
              if (panel) setActiveTab(panel as Tab);
            }}
          />
        )}
      </div>
    </div>
  );
}

export default App;
